// code-guardian-ignore-duplicates: shared presentational scaffolding (table/modal layout from the design system)
import { Fragment, useState, useEffect, useId, useRef, type ReactNode, type RefObject } from 'react';
import { Download, Store, User, MapPin, X, ClipboardList, ChevronDown, ChevronRight } from 'lucide-react';
import { BlockLoader } from '../loading';
import { buildCsv, downloadCsv } from '../../utils/csv';
import { getImageViewUrl } from '../../utils/imageUrl';
import { formatDate } from '../../utils/dateHelpers';

/**
 * The visits pop-up: every visit in a list, one row each, with its sign-in and sign-out,
 * the time spent beside the suggested time, and the task responses sent in on it.
 *
 * Shared by Reports › Store Visits (a row's Actions icon: one manager's or one store's
 * visits in the date range) and Reports › Store Visit Compliance (one person's visits on
 * a day, or in a week). Each page hands it the visits; it fetches nothing itself.
 */

// One task submission made during a visit (the "activity").
export interface VisitActivity {
  submissionId: string;
  taskTitle: string;
  submittedAt: string;
  answers: { question: string | null; answer: string | null; photoUrl: string | null }[];
}

// One manager check-in/out session, enriched with store + manager info + its activities.
export interface VisitRow {
  sessionId: number;
  managerCode: string;
  managerName: string;
  /** The visiting manager's own manager. AM/TH are hop distance on the reporting graph,
   *  not roles — and because the rows here are already MANAGERS, the visitor IS the
   *  Assistant Manager and this one-hop parent is the Trade Head. */
  thCode: string | null;
  thName: string | null;
  storeId: string;
  storeName: string;
  storeCode: string | null;
  city: string | null;
  state: string | null;
  category: string | null;
  trade: string | null;
  date: string;                 // YYYY-MM-DD (IST) check-in day
  checkInTime: string;          // ISO
  checkOutTime: string | null;  // ISO; null = still checked in
  durationMinutes: number | null;
  /** The phone's Visit Agenda suggested time, as saved when the visit started. Missing on
   *  visits signed in before it was kept, on task-only visits, at a store outside the
   *  person's handled list and for a person at their own store — the page then shows the
   *  time spent alone, as it always did. */
  suggestedMinutes?: number | null;
  activities: VisitActivity[];
  /** Store Visit Compliance only: false on a visit its count leaves out so far, a sign-in
   *  not yet signed out at a store with nothing else counted that day. Left out, as the
   *  Store Visits report sends it, the visit carries no such mark. */
  counted?: boolean;
}

/** Store: the visits made at one store (first column the manager). Manager: the visits one person made (first column the store). */
export type VisitsModalMode = 'store' | 'manager';

export const formatDuration = (mins: number | null): string => {
  if (mins == null) return '—';
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
};

export const formatTime = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true });
};

export const safeDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  try { return formatDate(iso); } catch { return String(iso).slice(0, 10); }
};

const formatSubmittedAt = (iso: string): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso);
  return `${safeDate(iso)}, ${formatTime(iso)}`;
};

// Compact renderer for a single answer value.
const renderAnswer = (a: { answer: string | null; photoUrl: string | null }): string => {
  if (a.answer && a.answer.trim() !== '') return a.answer;
  if (a.photoUrl) return '📷 Photo';
  return '—';
};

// The moment the phone's Visit Agenda time started being saved on each visit: the server
// carrying PR #662 (migrations/100_store_attendance_suggested_minutes.sql) went live at
// 1:46 pm IST on 13 Oct 2026, so a visit signed in earlier that day has none either.
// Nothing was back-filled and nothing is made up here: an agenda depended on what was open
// at the store that day, which is not known afterwards, so an older visit says "Not recorded".
const SUGGESTED_RECORDED_FROM = '2026-10-13T13:46:00+05:30';
const SUGGESTED_RECORDED_FROM_MS = Date.parse(SUGGESTED_RECORDED_FROM);
// The same moment in words, as the hovers say it. Written out so it reads in IST whatever
// the viewer's own time zone.
export const SUGGESTED_RECORDED_FROM_LABEL = '13 Oct 2026, 1:46 pm';

// Exact minutes, for the hover words ("51 min, suggested 15 min").
const inMinutes = (m: number): string => `${m} min`;

// Signed in before suggested times were saved. A sign-in time that won't parse falls back
// to the visit's IST day.
const signedInBeforeSuggestions = (v: VisitRow): boolean => {
  const t = Date.parse(v.checkInTime);
  if (Number.isNaN(t)) return !!v.date && v.date < SUGGESTED_RECORDED_FROM.slice(0, 10);
  return t < SUGGESTED_RECORDED_FROM_MS;
};

// One person at one store, for "does another visit of theirs here have a suggestion".
const visitPairKey = (v: VisitRow): string => `${v.managerCode}|${v.storeId}`;

// Why a visit has no suggested time, most certain first: a task visit (no sign-in), signed in
// before suggested times were saved, a store outside the person's handled list, or none saved.
//
// The handled list is the page's (handledStoreIdsByManager), the report's own as it stands now,
// while the agenda checked its own at sign-in. So the store is named as outside it only when
// the data shows it: this person's list is known (empty = not resolved), the store is not on
// it, and no other visit of theirs to this store in view has a suggestion (`suggestedAt`),
// which would show the agenda did suggest a time there. The words say what holds now.
type NotRecordedCause = 'task' | 'early' | 'notHandled' | 'unsaved';
const notRecordedCause = (
  v: VisitRow,
  handledStoreIdsByManager?: Record<string, string[]>,
  suggestedAt?: Set<string>,
): NotRecordedCause => {
  if (!v.checkInTime) return 'task';
  if (signedInBeforeSuggestions(v)) return 'early';
  const handled = handledStoreIdsByManager?.[v.managerCode];
  if (handled && handled.length > 0 && !handled.some((id) => String(id) === String(v.storeId))
    && !suggestedAt?.has(visitPairKey(v))) {
    return 'notHandled';
  }
  return 'unsaved';
};

// The hover on one visit's "Not recorded".
const NOT_RECORDED_WHY: Record<NotRecordedCause, string> = {
  task: 'Task visits have no sign-in, so no suggested time is saved',
  early: `Suggested times are saved from ${SUGGESTED_RECORDED_FROM_LABEL}`,
  // Today's handled list, not the one at sign-in, so it is told as a fact of today, not as the cause.
  notHandled: "No suggested time was saved when this visit started. This store isn't in their handled list today",
  unsaved: 'No suggested time was saved when this visit started',
};
const notRecordedReason = (
  v: VisitRow,
  handledStoreIdsByManager?: Record<string, string[]>,
  suggestedAt?: Set<string>,
): string => NOT_RECORDED_WHY[notRecordedCause(v, handledStoreIdsByManager, suggestedAt)];

// The hover on a whole set of visits none of which has a suggested time (the Manager View's
// Suggested Time cell). Their shared cause when they have one; task visits never have a
// suggestion, so they don't stand in the way of the signed-in visits' cause. Mixed causes
// point to the pop-up, which gives each visit's.
export const noneRecordedReason = (visits: VisitRow[], handledStoreIdsByManager?: Record<string, string[]>): string => {
  if (visits.length === 1) return notRecordedReason(visits[0], handledStoreIdsByManager);
  const lead = `None of these ${visits.length} visits has a suggested time`;
  const signedIn = visits.filter((v) => v.checkInTime);
  if (signedIn.length === 0) return `${lead}. ${NOT_RECORDED_WHY.task}`;
  const causes = new Set(signedIn.map((v) => notRecordedCause(v, handledStoreIdsByManager)));
  if (causes.size > 1) return `${lead}. Open their visits to see why for each`;
  const [cause] = causes;
  if (cause === 'notHandled') {
    return new Set(signedIn.map((v) => v.storeId)).size > 1
      ? `${lead}. No suggested time was saved when these visits started. These stores aren't in their handled list today`
      : `${lead}. No suggested time was saved when these visits started. This store isn't in their handled list today`;
  }
  if (cause === 'unsaved') return `${lead}. No suggested time was saved when these visits started`;
  return `${lead}. ${NOT_RECORDED_WHY[cause]}`;
};

// Trim only. buildCsv() does the quoting: it wraps every cell, doubles embedded `"`
// (RFC-4180) and folds newlines, so escaping here as well double-escapes (`A "B"`
// opened in Excel as `A ""B""`). The trim stays so exports are otherwise unchanged.
export const csvCell = (v: unknown): string => String(v ?? '').trim();

// ─── Manager pop-up tabs ─────────────────────────────────────────────────────
// A manager's pop-up has two lists — the visits made and the handled stores still to be
// visited — each with its own Download. This strip sits under the header of both and
// switches between them, so the pending list is reachable from the Actions icon and not
// only from the Pending Stores count.
export interface ManagerTabs {
  active: 'visits' | 'pending';
  visitCount: number;
  /** null = no handled stores resolved for this manager, so there is nothing to be pending against. */
  pendingCount: number | null;
  onSelect: (tab: 'visits' | 'pending') => void;
}

// The strip copies StoreMasterPage's (light tray, white pill, icon, count in brackets).
export function ManagerTabStrip({ tabs }: { tabs: ManagerTabs }) {
  const tabCls = (on: boolean) => `flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold sm:px-4 transition-colors ${
    on ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'
  } disabled:cursor-not-allowed disabled:text-gray-400`;
  return (
    <div className="border-b border-gray-100 px-6 py-2.5">
      <div className="flex w-fit flex-wrap items-center gap-1 rounded-lg border border-gray-100 bg-gray-50 p-1" role="tablist" aria-label="Manager lists">
        <button type="button" role="tab" aria-selected={tabs.active === 'visits'} className={tabCls(tabs.active === 'visits')}
          onClick={() => tabs.onSelect('visits')}>
          <MapPin className="hidden h-4 w-4 sm:block" aria-hidden="true" />
          Visits ({tabs.visitCount})
        </button>
        <button type="button" role="tab" aria-selected={tabs.active === 'pending'} className={tabCls(tabs.active === 'pending')}
          disabled={tabs.pendingCount == null}
          title={tabs.pendingCount == null ? 'No handled stores resolved for this manager' : 'Handled stores not visited in this date range'}
          onClick={() => tabs.onSelect('pending')}>
          <Store className="hidden h-4 w-4 sm:block" aria-hidden="true" />
          Pending stores ({tabs.pendingCount ?? '—'})
        </button>
      </div>
    </div>
  );
}

// ─── One visit, column by column ─────────────────────────────────────────────
// From lg up the visits are a table. Below it each visit is a small card: the header row
// is hidden and every value carries its column's name above it.
function CellLabel({ children }: { children: ReactNode }) {
  return <span className="mb-0.5 block text-[11px] font-medium uppercase tracking-wide text-gray-400 lg:hidden">{children}</span>;
}

// A muted dash with the reason on hover.
function Dash({ why }: { why: string }) {
  return <span className="cursor-help text-sm text-gray-400" title={why}>—</span>;
}

// Time spent, number first. With a suggested time on record a slim bar sets the two side by
// side: green at or above the suggestion, amber below it (the number turns amber too). The
// exact minutes are on hover. A visit with no suggestion shows the number alone.
function TimeSpentValue({ visit }: { visit: VisitRow }) {
  if (!visit.checkInTime) return <Dash why="A task visit has no sign-in or sign-out, so no time spent" />;
  if (!visit.checkOutTime || visit.durationMinutes == null) {
    return <Dash why="Still at the store: the time spent shows once they sign out" />;
  }
  const spent = visit.durationMinutes;
  const suggested = visit.suggestedMinutes ?? null;
  if (suggested == null) {
    return <span className="text-sm font-semibold tabular-nums text-gray-900" title={inMinutes(spent)}>{formatDuration(spent)}</span>;
  }
  const met = spent >= suggested;
  // Far beyond the suggestion (over three times it, or eight hours) is not a model visit:
  // it is usually a forgotten sign-out that the end-of-day job closed. Drawn neutral.
  const unusual = spent > suggested * 3 || spent > 8 * 60;
  // A sliver stays visible for a short stay; a 0-minute visit shows an empty track.
  const fill = spent > 0 ? Math.max(4, Math.min(100, Math.round((spent / suggested) * 100))) : 0;
  const title = unusual
    ? `${inMinutes(spent)}, suggested ${inMinutes(suggested)}. Much longer than suggested: the sign-out may have been closed automatically`
    : `${inMinutes(spent)}, suggested ${inMinutes(suggested)}`;
  return (
    <span className="inline-flex cursor-help flex-col items-start gap-1 lg:items-center" title={title}>
      <span className={`text-sm font-semibold tabular-nums ${met ? 'text-gray-900' : 'text-amber-700'}`}>{formatDuration(spent)}</span>
      <span className="block h-1.5 w-16 overflow-hidden rounded-full bg-gray-100" aria-hidden="true">
        <span className={`block h-full rounded-full ${unusual ? 'bg-gray-300' : met ? 'bg-emerald-500' : 'bg-amber-400'}`} style={{ width: `${fill}%` }} />
      </span>
    </span>
  );
}

// A quiet "Not recorded" with the reason on hover. Also the Manager View's Suggested Time
// cell when none of a manager's visits has one.
export function NotRecorded({ why }: { why: string }) {
  return (
    <span className="cursor-help text-xs text-gray-400 underline decoration-gray-300 decoration-dotted underline-offset-2" title={why}>
      Not recorded
    </span>
  );
}

// The suggested minutes, or a quiet "Not recorded" saying why on hover.
function SuggestedValue({ visit, handledStoreIdsByManager, suggestedAt }: {
  visit: VisitRow;
  handledStoreIdsByManager?: Record<string, string[]>;
  suggestedAt?: Set<string>;
}) {
  const n = visit.suggestedMinutes;
  if (n != null) {
    return (
      <span className="cursor-help text-sm tabular-nums text-gray-700" title={`The visit agenda suggested ${inMinutes(n)} when this visit started`}>
        {formatDuration(n)}
      </span>
    );
  }
  return <NotRecorded why={notRecordedReason(visit, handledStoreIdsByManager, suggestedAt)} />;
}

function SignedInValue({ visit }: { visit: VisitRow }) {
  if (visit.checkInTime) return <span className="text-sm tabular-nums text-gray-900">{formatTime(visit.checkInTime)}</span>;
  return (
    <span className="inline-flex cursor-help items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600"
      title="Tasks were submitted at this store without signing in">
      Task visit
    </span>
  );
}

function SignedOutValue({ visit }: { visit: VisitRow }) {
  if (visit.checkOutTime) return <span className="text-sm tabular-nums text-gray-900">{formatTime(visit.checkOutTime)}</span>;
  if (!visit.checkInTime) return <Dash why="A task visit has no sign-out" />;
  // Normally only today's visits are open: a forgotten sign-out is closed by the hourly
  // end-of-day job (store-visit-autoclose-scheduler.service.ts) when it is switched on.
  const pill = (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700"
      title="Still signed in at this store">
      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary-500" aria-hidden="true" />
      In progress
    </span>
  );
  if (visit.counted !== false) return pill;
  // Compliance counts a sign-in once it is signed out: until then the day's number leaves it out.
  return (
    <span className="inline-flex flex-col items-start gap-0.5 lg:items-center">
      {pill}
      <span className="cursor-help text-[11px] text-gray-400" title="Store Visit Compliance counts this visit once they sign out">
        Not counted yet
      </span>
    </span>
  );
}

// ─── Keyboard ────────────────────────────────────────────────────────────────
// A pop-up's keyboard manners, as ProbationReviewModal's: on opening, focus moves to its title
// (so a screen reader announces the dialog); Tab and Shift+Tab stay inside it rather than
// wandering into the page behind the overlay; Escape closes it; and on closing, focus goes
// back to whatever opened it. Shared with the Store Visits report's Pending stores pop-up.
const FOCUSABLE = 'a[href], button:not(:disabled), select:not(:disabled), input:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

export function useDialogKeyboard(open: boolean, onClose: () => void): {
  panelRef: RefObject<HTMLDivElement | null>;
  titleRef: RefObject<HTMLHeadingElement | null>;
} {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  // The latest onClose, for the key handler that outlives the render that made it.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    titleRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCloseRef.current();
        return;
      }
      const panel = panelRef.current;
      if (event.key !== 'Tab' || !panel) return;
      const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      const outside = !active || !panel.contains(active);
      if (event.shiftKey && (active === first || active === titleRef.current || outside)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || outside)) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      // Not when the opener has gone, as a tab of the other pop-up has.
      if (opener?.isConnected) opener.focus();
    };
  }, [open]);

  return { panelRef, titleRef };
}

// ─── Drill-down modal ────────────────────────────────────────────────────────
export default function VisitsModal({
  open, mode, title, subtitle, sessions, rangeLabel, fileKey, tabs, handledStoreIdsByManager, loading = false, error = null, onClose,
}: {
  open: boolean;
  mode: VisitsModalMode;
  title: string;
  subtitle: string;
  sessions: VisitRow[];
  rangeLabel: string;
  /** Manager code (Manager View) or store code (Store View), for the download's file name. */
  fileKey: string;
  /** Manager View only. */
  tabs?: ManagerTabs;
  /** The report's handled stores by manager code, when the page has them: lets a "Not
   *  recorded" add that the store isn't in the person's handled list today (see notRecordedCause). */
  handledStoreIdsByManager?: Record<string, string[]>;
  /** The visits are still being fetched, by a page that fetches them on opening. */
  loading?: boolean;
  /** Why the visits could not be fetched, shown in place of the list. */
  error?: string | null;
  onClose: () => void;
}) {
  // Which visits have their activity expanded (keyed by sessionId).
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  // Reset the expanded set whenever a different row is opened.
  useEffect(() => { setExpanded(new Set()); }, [title, subtitle]);
  const { panelRef, titleRef } = useDialogKeyboard(open, onClose);
  const titleId = useId();
  if (!open) return null;

  const toggle = (id: number) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // Newest visit first: by day, then by sign-in time (as the Full list's Visits sheet). The
  // day comes first so a task visit, which has no sign-in time, sits among the visits of its
  // own day instead of falling to the very bottom.
  const ordered = [...sessions].sort((a, b) =>
    (b.date || '').localeCompare(a.date || '') || (b.checkInTime || '').localeCompare(a.checkInTime || ''));
  // Person + store pairs where some visit in view has a suggested time, so a "Not recorded"
  // there never blames the handled list.
  const suggestedAt = new Set(sessions.filter((s) => s.suggestedMinutes != null).map(visitPairKey));

  // The table's columns, in order. A hint is the header's hover explanation.
  const visitColumns: { label: string; hint?: string }[] = [
    { label: mode === 'store' ? 'Manager' : 'Store' },
    { label: 'Date' },
    { label: 'Signed in' },
    { label: 'Signed out' },
    { label: 'Time spent', hint: 'Time between sign-in and sign-out. The bar compares it with the suggested time: green at or above it, amber below' },
    { label: 'Suggested', hint: `The time the phone's visit agenda suggested, as saved when the visit started (saved from ${SUGGESTED_RECORDED_FROM_LABEL})` },
    { label: 'Activities' },
  ];
  // A grid item in a phone card, a cell from lg up. The first column is left-aligned and may
  // wrap; the rest are centred on one line. Kept as two lists rather than overriding one:
  // Tailwind emits text-center after text-left (and nowrap after normal), so an override
  // added on the same cell would lose.
  const visitCellBase = 'block lg:table-cell lg:py-3.5 lg:align-middle';
  const visitCell = `${visitCellBase} lg:whitespace-nowrap lg:px-3 lg:text-center`;
  const visitFirstCell = `${visitCellBase} col-span-full lg:pl-5 lg:pr-3 lg:text-left`;

  // One line per visit, in the order on screen. Task titles ride along so the file says what
  // was done on a visit without the responses (those stay in the expandable rows).
  // Suggested Minutes is the time the phone suggested for the visit, left blank (not '-')
  // when none was recorded, so the column stays a column of numbers.
  const download = () => {
    const headers = ['Manager', 'Manager Code', 'Store', 'Store Code', 'City', 'State', 'Store Category', 'Trade',
      'Visit Date', 'Check In', 'Check Out', 'Time Spent', 'Minutes Spent', 'Suggested Minutes', 'Activities', 'Tasks Submitted'];
    const data = ordered.map((v) => [
      v.managerName, v.managerCode, v.storeName, v.storeCode || '-', v.city || '-', v.state || '-', v.category || '-', v.trade || '-',
      v.date ? safeDate(v.date) : '-',
      v.checkInTime ? formatTime(v.checkInTime) : 'No check-in (task visit)',
      v.checkOutTime ? formatTime(v.checkOutTime) : (v.checkInTime ? 'In progress' : '-'),
      v.durationMinutes == null ? '-' : formatDuration(v.durationMinutes),
      v.durationMinutes == null ? '-' : String(v.durationMinutes),
      v.suggestedMinutes == null ? '' : String(v.suggestedMinutes),
      String(v.activities.length),
      v.activities.map((a) => a.taskTitle).filter(Boolean).join('; ') || '-',
    ].map(csvCell));
    const prefix = mode === 'store' ? 'store-visits-store' : 'store-visits-manager';
    downloadCsv(`${prefix}-${fileKey || 'list'}-${rangeLabel.replace(/\s+/g, '')}.csv`, buildCsv(headers, data));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby={titleId}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div ref={panelRef} className="relative z-10 flex max-h-[85vh] w-full max-w-5xl flex-col rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-primary-600">
              {mode === 'store' ? <Store className="h-3.5 w-3.5" /> : <User className="h-3.5 w-3.5" />}
              {mode === 'store' ? 'Store visits' : 'Manager visits'}
            </div>
            <h2 id={titleId} ref={titleRef} tabIndex={-1} className="mt-0.5 truncate text-lg font-semibold text-gray-900 focus:outline-none">{title}</h2>
            {subtitle && <p className="truncate text-sm text-gray-500">{subtitle}</p>}
          </div>
          <div className="flex items-center gap-3">
            {/* Not while the visits are still coming, or could not be read: it would say 0. */}
            {!loading && !error && (
              <span className="whitespace-nowrap rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
                {ordered.length} visit{ordered.length === 1 ? '' : 's'}
              </span>
            )}
            <button
              type="button"
              onClick={download}
              disabled={ordered.length === 0}
              className="flex items-center gap-1.5 rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <Download className="h-3.5 w-3.5" /> Download
            </button>
            <button type="button" onClick={onClose} className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600" aria-label="Close">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {tabs && <ManagerTabStrip tabs={tabs} />}

        {/* Body — the visits as one table on a soft grey canvas. From lg up it is a table
            with a sticky header; below that each visit is a small card (see CellLabel). */}
        <div className="flex min-h-0 flex-1 flex-col rounded-b-2xl bg-gray-50 p-4 sm:p-5">
          <div className="min-h-0 overflow-y-auto rounded-2xl border border-gray-200 bg-white shadow-sm">
            {loading ? (
              <BlockLoader className="py-10" />
            ) : error ? (
              <p className="py-10 text-center text-sm text-red-700">{error}</p>
            ) : ordered.length === 0 ? (
              <p className="py-10 text-center text-sm text-gray-500">No visits in this date range.</p>
            ) : (
              // border-separate: with collapsed borders the line under the header
              // scrolls away while the header stays.
              <table className="w-full border-separate border-spacing-0">
                <thead className="hidden lg:table-header-group">
                  <tr>
                    {visitColumns.map((c, i) => (
                      <th
                        key={c.label}
                        scope="col"
                        title={c.hint}
                        className={`sticky top-0 z-10 whitespace-nowrap border-b border-gray-200 bg-gray-50 px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500 ${
                          i === 0 ? 'pl-5 text-left' : 'text-center'
                        } ${c.hint ? 'cursor-help' : ''}`}
                      >
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="block divide-y divide-gray-100 lg:table-row-group">
                  {ordered.map((v) => {
                    const isOpen = expanded.has(v.sessionId);
                    const hasActivity = v.activities.length > 0;
                    return (
                      <Fragment key={v.sessionId}>
                        <tr className="grid grid-cols-2 gap-x-4 gap-y-3 px-4 py-4 transition-colors hover:bg-gray-50/70 sm:grid-cols-3 lg:table-row lg:p-0">
                          <td className={visitFirstCell}>
                            <div className="text-sm font-semibold text-gray-900">
                              {mode === 'store' ? v.managerName : v.storeName}
                            </div>
                            <div className="text-xs text-gray-500">
                              {mode === 'store' ? v.managerCode : (v.storeCode || v.city || '')}
                            </div>
                          </td>
                          {/* On a phone the date and the Activities button go last, so the
                              times being compared sit side by side. */}
                          <td className={`${visitCell} max-sm:order-1`}>
                            <CellLabel>Date</CellLabel>
                            <span className="text-sm text-gray-700">{safeDate(v.date)}</span>
                          </td>
                          <td className={visitCell}><CellLabel>Signed in</CellLabel><SignedInValue visit={v} /></td>
                          <td className={visitCell}><CellLabel>Signed out</CellLabel><SignedOutValue visit={v} /></td>
                          <td className={visitCell}><CellLabel>Time spent</CellLabel><TimeSpentValue visit={v} /></td>
                          <td className={visitCell}><CellLabel>Suggested</CellLabel>
                            <SuggestedValue visit={v} handledStoreIdsByManager={handledStoreIdsByManager} suggestedAt={suggestedAt} />
                          </td>
                          {/* No phone label: the button names itself. self-end keeps it on the
                              line of the values beside it. */}
                          <td className={`${visitCell} self-end max-sm:order-2`}>
                            <button
                              type="button"
                              onClick={() => hasActivity && toggle(v.sessionId)}
                              disabled={!hasActivity}
                              aria-expanded={hasActivity ? isOpen : undefined}
                              className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                                hasActivity ? 'border-primary-200 bg-primary-50 text-primary-700 hover:bg-primary-100' : 'border-gray-200 text-gray-400 cursor-default'
                              }`}
                              title={hasActivity ? 'Show task responses' : 'No task activity recorded'}
                            >
                              <ClipboardList className="h-3.5 w-3.5" />
                              {v.activities.length} {v.activities.length === 1 ? 'activity' : 'activities'}
                              {hasActivity && (isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />)}
                            </button>
                          </td>
                        </tr>

                        {/* Activity detail, under its visit across the whole width */}
                        {isOpen && hasActivity && (
                          <tr className="block lg:table-row">
                            <td colSpan={visitColumns.length} className="block bg-gray-50/70 px-4 py-3 lg:table-cell lg:px-5">
                              <div className="space-y-3">
                                {v.activities.map((act) => (
                                  <div key={act.submissionId} className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
                                    <div className="flex items-center justify-between gap-2">
                                      <span className="text-sm font-semibold text-gray-800">{act.taskTitle}</span>
                                      <span className="whitespace-nowrap text-xs text-gray-500">{formatSubmittedAt(act.submittedAt)}</span>
                                    </div>
                                    {act.answers.length > 0 ? (
                                      <dl className="mt-2 space-y-1">
                                        {act.answers.map((ans, i) => {
                                          // photoUrl is the RAW Azure blob path (e.g. "PROD/task-photos/…selfie.jpg").
                                          // Using it as an href resolves relative to the current /reports/ page, so the
                                          // SPA swallows it and opens a blank tab. Serve it through the authenticated
                                          // /api/images/view proxy via the shared helper (same as the other report pages).
                                          const photoHref = getImageViewUrl(ans.photoUrl);
                                          return (
                                            <div key={i} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-2 text-sm">
                                              <dt className="truncate text-gray-500">{ans.question || 'Response'}</dt>
                                              <dd className="text-gray-900">
                                                {photoHref
                                                  ? <a href={photoHref} target="_blank" rel="noreferrer" className="text-primary-600 underline">📷 Photo</a>
                                                  : renderAnswer(ans)}
                                              </dd>
                                            </div>
                                          );
                                        })}
                                      </dl>
                                    ) : (
                                      <p className="mt-1 text-xs text-gray-400">Submitted (no field responses).</p>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
